import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArrowRight, MailCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { homeFor } from "@/App";
import AuthShell, { inputCls, labelCls, primaryBtn, errText } from "@/components/AuthShell";
import { api } from "@/lib/api";

export default function Login({ area = "admin", register = false }) {
  const { user, clearSession } = useAuth();
  useEffect(() => { if (user?.demo) clearSession(); }, [user, clearSession]);
  if (user && !user.demo) return <Navigate to={homeFor(user)} replace />;
  if (register) return <Register />;
  return <SignIn area={area} />;
}

function SignIn({ area }) {
  const { login, verifyMfa, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const isAdmin = area === "admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");
  const [unverified, setUnverified] = useState(false);

  const finish = (u) => {
    if ((u.role === "customer") !== !isAdmin) toast.error(isAdmin ? "Use the Customer Login for portal accounts" : "Operator accounts must use Admin Login");
    toast.success("Session established");
    nav(homeFor(u));
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setUnverified(false);
    try {
      const d = await login(email, password, remember);
      if (d.mfa_required) { setMfaToken(d.mfa_token); return; }
      finish(d.user);
    } catch (err) {
      if (err?.response?.status === 403 && /verify/i.test(err.response.data?.detail || "")) setUnverified(true);
      toast.error(errText(err, "Login failed"));
    } finally { setBusy(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault(); setBusy(true);
    try { finish(await verifyMfa(mfaToken, code)); }
    catch (err) { toast.error(errText(err, "Verification failed")); if (err?.response?.status === 401 && /expired/i.test(err.response.data?.detail || "")) setMfaToken(null); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    await api.post("/auth/resend-verification", { email });
    toast.success("Verification email sent — check your inbox");
  };

  if (mfaToken) {
    return (
      <AuthShell kicker="Two-factor authentication" title="Enter your code" sub="Open your authenticator app and enter the 6-digit code, or use one of your recovery codes." testid="mfa-step">
        <form onSubmit={submitMfa}>
          <label className={labelCls}>Verification code</label>
          <input data-testid="mfa-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} className={`${inputCls} mb-6 text-center text-xl tracking-[0.4em]`} placeholder="000000" required />
          <button data-testid="mfa-submit" disabled={busy} className={primaryBtn}>{busy ? "VERIFYING…" : "VERIFY"} <KeyRound size={14} /></button>
        </form>
        <button type="button" data-testid="mfa-back" onClick={() => setMfaToken(null)} className="mt-4 text-xs text-zinc-500 hover:text-zinc-300">← Back to sign in</button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={isAdmin ? "Admin sign in" : "Customer sign in"} sub={isAdmin ? "SOC operators and administrators only. All sessions are audited." : "Access your organisation's security dashboard."} testid={`login-${area}`}>
      <form onSubmit={submit}>
        <label className={labelCls}>Email</label>
        <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mb-4`} required autoComplete="email" />
        <label className={labelCls}>Password</label>
        <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} mb-4`} required autoComplete="current-password" />
        <div className="flex items-center justify-between mb-6 text-xs">
          <label className="flex items-center gap-2 text-zinc-400 cursor-pointer"><input data-testid="login-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-cyan-400" /> Remember me for 30 days</label>
          <Link to={`/forgot-password?area=${area}`} data-testid="login-forgot-link" className="text-cyan-300 hover:underline">Forgot password?</Link>
        </div>
        {unverified && (
          <div data-testid="login-unverified" className="mb-4 border border-orange-400/40 bg-orange-400/5 p-3 text-xs text-orange-200 flex items-start gap-2">
            <MailCheck size={14} className="mt-0.5 shrink-0" /><div>Your email is not verified yet. <button type="button" data-testid="login-resend" onClick={resend} className="underline">Resend verification email</button></div>
          </div>
        )}
        <button data-testid="login-submit" disabled={busy} className={primaryBtn}>{busy ? "AUTHENTICATING…" : "AUTHENTICATE"} <ArrowRight size={14} /></button>
      </form>
      <div className="flex items-center gap-3 my-5 text-[10px] font-mono uppercase tracking-widest text-zinc-600"><span className="flex-1 border-t hairline" /> or <span className="flex-1 border-t hairline" /></div>
      <button type="button" data-testid="google-login-btn" onClick={loginWithGoogle} className="w-full flex items-center justify-center gap-3 border hairline bg-[#1B1B28] hover:bg-white/[0.04] text-sm py-3 font-medium transition-colors duration-150">
        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.5 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
        Continue with Google
      </button>
      <div className="mt-6 text-xs text-zinc-500 space-y-1">
        {isAdmin ? <div>Customer? <Link to="/customer/login" className="text-zinc-300 hover:underline">Customer Login</Link></div> : (
          <>
            <div>No account yet? <Link to="/customer/register" data-testid="login-register-link" className="text-cyan-300 hover:underline">Create an account</Link></div>
            <div>Operator? <Link to="/admin/login" className="text-zinc-300 hover:underline">Admin Login</Link></div>
          </>
        )}
      </div>
    </AuthShell>
  );
}

function Register() {
  const [params] = useSearchParams();
  const ref = (params.get("ref") || "").toUpperCase();
  useEffect(() => { const q = Number(params.get("qty")); if (q > 0) localStorage.setItem("cs_qty", String(q)); }, [params]);
  const [f, setF] = useState({ org_name: "", name: "", email: "", password: "", industry: "General", website: "", ref });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/register", f); setDone(data); }
    catch (err) { toast.error(errText(err, "Registration failed")); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <AuthShell kicker="Almost there" title="Check your inbox" testid="register-done">
        <div className="border border-cyan-400/30 bg-cyan-400/[0.04] p-4 text-sm text-zinc-300 space-y-2">
          <MailCheck size={22} className="text-cyan-300" />
          <p>We sent a verification link to <span className="font-mono text-cyan-300" data-testid="register-done-email">{done.email}</span>. Click it to activate your workspace for <strong>{done.org}</strong>{done.trial_days ? <> and start your {done.trial_days}-day trial</> : null}.</p>
          {done.email_status !== "sent" && <p data-testid="register-email-warning" className="text-xs text-orange-300">We could not deliver the email right now ({done.email_status}). Contact admin@dsouzainfotech.com and we will verify your account manually.</p>}
        </div>
        <Link to="/customer/login" data-testid="register-go-login" className="block mt-4 text-xs text-cyan-300 hover:underline">Already verified? Sign in →</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell kicker="Get started" title="Create your workspace" sub="From $15 per endpoint per year · billed annually · cancel anytime." testid="register-form">
      <form onSubmit={submit}>
        {ref && <div data-testid="reg-referral" className="mb-4 border border-cyan-400/40 bg-cyan-400/[0.05] p-2.5 text-xs text-cyan-200">Referral code <span className="font-mono">{ref}</span> applied — your referrer earns a free month when you subscribe.</div>}
        <label className={labelCls}>Organisation name</label>
        <input data-testid="reg-org" value={f.org_name} onChange={set("org_name")} className={`${inputCls} mb-4`} required minLength={2} placeholder="Acme Ltd" />
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Your name</label><input data-testid="reg-name" value={f.name} onChange={set("name")} className={`${inputCls} mb-4`} required placeholder="Jane Doe" /></div>
          <div><label className={labelCls}>Industry</label>
            <select data-testid="reg-industry" value={f.industry} onChange={set("industry")} className={`${inputCls} mb-4`}>
              {["General", "Technology", "Healthcare", "Finance", "Legal", "Manufacturing", "Education", "Retail", "Government", "MSP / MSSP"].map((i) => <option key={i}>{i}</option>)}
            </select></div>
        </div>
        <label className={labelCls}>Work email</label>
        <input data-testid="reg-email" type="email" value={f.email} onChange={set("email")} className={`${inputCls} mb-4`} required autoComplete="email" placeholder="you@company.com" />
        <label className={labelCls}>Password</label>
        <input data-testid="reg-password" type="password" value={f.password} onChange={set("password")} className={`${inputCls} mb-2`} required minLength={10} autoComplete="new-password" placeholder="Minimum 10 characters" />
        <input type="text" name="website" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />
        <p className="text-[11px] text-zinc-500 mb-6">By continuing you agree to our <Link to="/legal/terms" className="underline">Terms</Link> and <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.</p>
        <button data-testid="reg-submit" disabled={busy} className={primaryBtn}>{busy ? "CREATING WORKSPACE…" : "CREATE WORKSPACE"} <ArrowRight size={14} /></button>
      </form>
      <div className="mt-6 text-xs text-zinc-500">Already have an account? <Link to="/customer/login" data-testid="reg-login-link" className="text-cyan-300 hover:underline">Sign in</Link></div>
    </AuthShell>
  );
}
